import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Starter Sparkle — upkeep clean",
  "Deep & Detailed — deep clean",
  "Honey-Do Combo — clean + handyman",
  "Handyman only",
  "Something else",
];

const CITIES = [
  "Port St. Lucie",
  "St. Lucie West",
  "Tradition",
  "Fort Pierce",
  "Jensen Beach",
  "Stuart",
  "Palm City",
  "Hutchinson Island",
  "Other / Not sure",
];

export function EstimateDialog({ label = "Get a Same-Day Estimate", service, className = "", testId = "estimate-dialog-trigger" }) {
  const defaultService = SERVICES.find((s) => service && s.startsWith(service)) || SERVICES[0];
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", city: CITIES[0], service: defaultService, message: "" });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Request received!", {
        description: "We'll call you back today with a free estimate.",
      });
      setOpen(false);
      setForm({ name: "", phone: "", email: "", address: "", city: CITIES[0], service: defaultService, message: "" });
    } catch {
      toast.error("Something went wrong", { description: "Please call us at 772-667-3007 instead." });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={testId} className={className}>{label}</button>
      </DialogTrigger>
      <DialogContent className="bg-[#FDFBF7] border-[#D96B43]/20 sm:max-w-md" data-testid="estimate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0F172A]">Same-Day Estimate</DialogTitle>
          <DialogDescription className="text-[#475569]">
            Tell us what's on your list — we reply today. Licensed &amp; insured, no obligation.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 pt-2">
          <Input
            data-testid="estimate-name-input"
            required
            placeholder="Your name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-white border-[#0F172A]/15 focus-visible:ring-[#D96B43]"
          />
          <Input
            data-testid="estimate-phone-input"
            required
            placeholder="Phone number"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="bg-white border-[#0F172A]/15 focus-visible:ring-[#D96B43]"
          />
          <Input
            data-testid="estimate-email-input"
            type="email"
            placeholder="Email address"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="bg-white border-[#0F172A]/15 focus-visible:ring-[#D96B43]"
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              data-testid="estimate-address-input"
              placeholder="Street address (optional)"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="bg-white border-[#0F172A]/15 focus-visible:ring-[#D96B43]"
            />
            <select
              data-testid="estimate-city-select"
              required
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              className="h-9 rounded-md border border-[#0F172A]/15 bg-white px-3 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#D96B43]"
            >
              {CITIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <select
            data-testid="estimate-service-select"
            value={form.service}
            onChange={(e) => setForm({ ...form, service: e.target.value })}
            className="w-full h-9 rounded-md border border-[#0F172A]/15 bg-white px-3 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#D96B43]"
          >
            {SERVICES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <Textarea
            data-testid="estimate-message-input"
            placeholder="What's on your honey-do list? (optional)"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="bg-white border-[#0F172A]/15 focus-visible:ring-[#D96B43] min-h-[90px]"
          />
          <Button
            data-testid="estimate-submit-button"
            type="submit"
            disabled={sending}
            className="w-full bg-[#0F172A] hover:bg-[#D96B43] text-white transition-colors duration-300"
          >
            {sending ? "Sending…" : "Request My Free Estimate"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
